import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import Header from '../components/Header';
import { auth } from '../config/firebase';

/**
 * Set or reset a password from an emailed link, on the app's own domain.
 *
 * Replaces Firebase's hosted handler (firebaseapp.com/__/auth/action), which
 * is unbranded and shows as a bare globe tab with no title. The setup email
 * from scripts/provision-owner.js links here with ?mode=resetPassword&oobCode=…
 *
 * Also safe to register later as the project's custom email action URL: any
 * mode other than resetPassword (email verification, email-change recovery)
 * is forwarded untouched to Firebase's own handler.
 *
 * Only the newest link for an account works — generating another cancels the
 * earlier code — so an "invalid" code usually means a newer email exists.
 */

const MIN_LENGTH = 8;
const FIREBASE_ACTION_HANDLER = `https://${process.env.REACT_APP_FIREBASE_AUTH_DOMAIN}/__/auth/action`;

type Status = 'checking' | 'ready' | 'saving' | 'done' | 'invalid';

/** Follow continueUrl only back into this app, never to another site. */
function safeNextPath(continueUrl: string | null): string {
  if (!continueUrl) return '/provider-login';
  try {
    const url = new URL(continueUrl, window.location.origin);
    return url.origin === window.location.origin ? `${url.pathname}${url.search}` : '/provider-login';
  } catch {
    return '/provider-login';
  }
}

function linkProblem(code?: string): string {
  switch (code) {
    case 'auth/expired-action-code':
      return 'This link has expired. Password links work for one hour.';
    case 'auth/invalid-action-code':
      return 'This link has already been used, or a newer email has replaced it. Only the most recent link works.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact your practice administrator.';
    case 'auth/user-not-found':
      return 'This account no longer exists. Contact your practice administrator.';
    default:
      return 'This link could not be verified.';
  }
}

const SetPassword: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const mode = params.get('mode');
  const oobCode = params.get('oobCode') || '';
  const nextPath = safeNextPath(params.get('continueUrl'));

  const [status, setStatus] = useState<Status>('checking');
  const [email, setEmail] = useState('');
  const [problem, setProblem] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const previous = document.title;
    document.title = 'Set Your Password | My ClinicCore';
    return () => { document.title = previous; };
  }, []);

  useEffect(() => {
    if (mode && mode !== 'resetPassword') {
      window.location.replace(`${FIREBASE_ACTION_HANDLER}?${params.toString()}`);
      return;
    }
    if (!oobCode) {
      setProblem('This link is incomplete. Open it directly from the email.');
      setStatus('invalid');
      return;
    }

    let cancelled = false;
    // Checks the code without using it up.
    verifyPasswordResetCode(auth, oobCode)
      .then((address) => {
        if (cancelled) return;
        setEmail(address);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setProblem(linkProblem(err?.code));
        setStatus('invalid');
      });
    return () => { cancelled = true; };
  }, [mode, oobCode, params]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("The passwords don't match.");
      return;
    }

    setError('');
    setStatus('saving');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('done');
    } catch (err: any) {
      if (err?.code === 'auth/expired-action-code' || err?.code === 'auth/invalid-action-code') {
        setProblem(linkProblem(err.code));
        setStatus('invalid');
        return;
      }
      setError(
        err?.code === 'auth/weak-password'
          ? 'Choose a stronger password.'
          : (err?.message || 'Could not save your password. Please try again.').replace(/^Firebase:\s*/, '')
      );
      setStatus('ready');
    }
  };

  const isSaving = status === 'saving';

  return (
    <div className="app">
      <Header />

      <main id="main-content" className="page">
        <div className="container">
          <div style={{
            maxWidth: '420px',
            margin: '0 auto',
            paddingTop: '3rem'
          }}>
            {/* Header */}
            <div className="text-center" style={{ marginBottom: '2rem' }}>
              <div style={{
                fontSize: '2rem',
                marginBottom: '0.5rem',
                textAlign: 'center'
              }}>
                My ClinicCore
              </div>
              <h1 className="page-title" style={{ marginBottom: '0.5rem' }}>
                {status === 'done' ? 'Password Saved' : 'Set Your Password'}
              </h1>
              <p style={{ color: '#d1d5db' }}>
                {status === 'done'
                  ? 'You can now sign in with your new password'
                  : 'Choose a password for your My ClinicCore account'}
              </p>
            </div>

            <div className="card">
              {status === 'checking' && (
                <p role="status" style={{ color: '#d1d5db', textAlign: 'center', margin: 0 }}>
                  Checking your link...
                </p>
              )}

              {status === 'invalid' && (
                <div>
                  <div className="form-error" role="alert" style={{ marginBottom: '1rem' }}>
                    {problem}
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                    To get a new link, go to the sign-in page, enter your email, and choose
                    "Forgot password?". Use the newest email you receive, and check your spam
                    folder if it doesn't arrive.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    onClick={() => navigate(nextPath)}
                  >
                    Go to Sign In
                  </button>
                </div>
              )}

              {(status === 'ready' || status === 'saving') && (
                <form onSubmit={handleSubmit} aria-label="Set password form">
                  {/* Account — read-only, and lets password managers save the pair */}
                  <div className="form-group">
                    <label htmlFor="set-password-email" className="form-label">
                      Account
                    </label>
                    <input
                      id="set-password-email"
                      type="email"
                      className="form-input"
                      value={email}
                      autoComplete="username"
                      readOnly
                    />
                  </div>

                  {/* New Password */}
                  <div className="form-group">
                    <label htmlFor="set-password-new" className="form-label">
                      New Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="set-password-new"
                        type={showPassword ? 'text' : 'password'}
                        className="form-input"
                        placeholder={`At least ${MIN_LENGTH} characters`}
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
                        disabled={isSaving}
                        autoComplete="new-password"
                        required
                        aria-required="true"
                        aria-invalid={error ? 'true' : undefined}
                        aria-describedby={error ? 'set-password-error' : undefined}
                        style={{ paddingRight: '3rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        style={{
                          position: 'absolute',
                          right: '0.75rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#d1d5db',
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                        disabled={isSaving}
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div className="form-group">
                    <label htmlFor="set-password-confirm" className="form-label">
                      Confirm Password
                    </label>
                    <input
                      id="set-password-confirm"
                      type={showPassword ? 'text' : 'password'}
                      className="form-input"
                      placeholder="Enter it again"
                      value={confirm}
                      onChange={(e) => { setConfirm(e.target.value); if (error) setError(''); }}
                      disabled={isSaving}
                      autoComplete="new-password"
                      required
                      aria-required="true"
                      aria-invalid={error ? 'true' : undefined}
                      aria-describedby={error ? 'set-password-error' : undefined}
                    />
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div id="set-password-error" className="form-error" role="alert" aria-live="assertive" style={{ marginBottom: '1rem' }}>
                      {error}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ width: '100%', marginBottom: '1rem' }}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid transparent',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        Saving...
                      </span>
                    ) : (
                      'Save Password'
                    )}
                  </button>
                </form>
              )}

              {status === 'done' && (
                <div className="text-center">
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }} aria-hidden="true">✅</div>
                  <p role="status" style={{ color: '#d1d5db', marginBottom: '1.5rem' }}>
                    The password for <strong>{email}</strong> is saved.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                    onClick={() => navigate(nextPath)}
                  >
                    Continue to Sign In
                  </button>
                </div>
              )}

              {/* Security Notice */}
              {(status === 'ready' || status === 'saving') && (
                <div style={{
                  marginTop: '1.5rem',
                  padding: '1rem',
                  backgroundColor: '#374151',
                  borderRadius: '0.5rem',
                  border: '1px solid #4b5563'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1rem' }}>🔒</span>
                    <h4 style={{
                      fontSize: '0.875rem',
                      color: '#D4A017',
                      margin: 0
                    }}>
                      Private to You
                    </h4>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#d1d5db', margin: 0 }}>
                    Your password is sent over an encrypted connection and is never visible to
                    anyone else, including your practice administrator.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="text-center" style={{ marginTop: '2rem' }}>
              <p style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
                Having trouble? Contact your practice administrator.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SetPassword;
